// The platform admin panel (/admin): every customer organization, plans, users, billing activity and the record of
// what admins did. Only platform admins get in (the API answers "not found" to anyone else).
import { createContext, useContext, useState, type FormEvent, type ReactNode } from "react";
import { Link, NavLink, Navigate, Outlet, useNavigate, useParams, useSearchParams } from "react-router";
import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, APP_NAME, api } from "../../lib/api";
import { formatShort, formatSize } from "../../lib/chat";
import { useMe } from "../../lib/session";
import { Alert, Avatar, Badge, Button, Card, Dialog, Field, Logo, Spinner, cx } from "../../components/ui";
import { Icon, type IconName } from "../../components/icons";
import { FullPageSpinner } from "../auth";
import { TwoFactorCard } from "../profile";

export const P = "/platform";
export const errorText = (e: unknown) => (e instanceof ApiError ? e.message : "Something went wrong. Please try again.");
const day = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString([], { day: "numeric", month: "short", year: "numeric" }) : "—");
const money = (amount: number, currency: string) => new Intl.NumberFormat(undefined, { style: "currency", currency: currency.toUpperCase(), maximumFractionDigits: 0 }).format(amount / 100);

// ---------------------------------------------------------------- layout

// This admin's role and what it allows (the API checks too; the panel hides what the role can't use).
type Role = "super" | "support" | "billing";
type PlatformMe = { role: Role; roleLabel: string; permissions: string[] };
const ROLE_LABEL: Record<Role, string> = { super: "Super admin", support: "Support", billing: "Billing" };
const PlatformContext = createContext<PlatformMe>({ role: "support", roleLabel: "Support", permissions: [] });
const useCan = () => { const p = useContext(PlatformContext); return (action: string) => p.permissions.includes(action); };

export function PlatformLayout() {
  const me = useMe();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const check = useQuery({ queryKey: ["platform-me"], queryFn: () => api<PlatformMe>(`${P}/me`), enabled: Boolean(me.data?.user.platformRole), retry: false });
  const [menuOpen, setMenuOpen] = useState(false);
  if (me.isLoading) return <FullPageSpinner />;
  if (!me.data) return <Navigate to="/login?next=/admin" replace />;
  if (!me.data.user.platformRole) {
    // Signed in as someone else (a demo account, say): say so, and offer to switch, rather than sending them to their
    // organization (which may not even open from this network).
    async function switchAccount() {
      await api("/auth/logout", { method: "POST" }).catch(() => undefined);
      qc.clear();
      navigate("/login?next=/admin", { replace: true });
    }
    return (
      <div className="grid min-h-dvh place-items-center p-6">
        <Card className="w-full max-w-md text-center">
          <div className="mb-4 flex justify-center"><Logo withName={false} /></div>
          <h1 className="text-lg font-semibold">This account isn't a platform admin</h1>
          <p className="mt-2 break-words text-sm text-slate-600 dark:text-slate-400">You're signed in as {me.data.user.email}. The admin panel is only for {APP_NAME}'s own admins.</p>
          <div className="mt-6 flex flex-wrap justify-center gap-2">
            <Button onClick={switchAccount}>Sign in as someone else</Button>
            <Link to="/"><Button variant="secondary">Back to {APP_NAME}</Button></Link>
          </div>
        </Card>
      </div>
    );
  }
  if (check.error instanceof ApiError && check.error.code === "MFA_SETUP_REQUIRED") {
    return (
      <div className="mx-auto max-w-2xl space-y-4 p-6 pt-16">
        <div className="flex items-center gap-2"><Logo /><Badge tone="amber">Admin</Badge></div>
        <Alert kind="info">The admin panel needs two-factor sign-in. Turn it on below, then the panel opens.</Alert>
        <TwoFactorCard />
        <Button variant="secondary" onClick={() => void check.refetch()}>Continue to the admin panel</Button>
      </div>
    );
  }
  if (check.error instanceof ApiError && check.error.code === "REAUTH_REQUIRED") {
    async function again() {
      await api("/auth/logout", { method: "POST" }).catch(() => undefined);
      qc.clear();
      navigate("/login?next=/admin", { replace: true });
    }
    return (
      <div className="grid min-h-dvh place-items-center p-6">
        <Card className="w-full max-w-md text-center">
          <div className="mb-4 flex justify-center"><Logo withName={false} /></div>
          <h1 className="text-lg font-semibold">Sign in again</h1>
          <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">{check.error.message}</p>
          <Button className="mt-6" onClick={again}>Sign in again</Button>
        </Card>
      </div>
    );
  }
  if (check.isLoading) return <FullPageSpinner />;

  const nav: { to: string; label: string; icon: IconName; end?: boolean }[] = [
    { to: "/admin", label: "Overview", icon: "home", end: true },
    { to: "/admin/organizations", label: "Organizations", icon: "team" },
    { to: "/admin/usage", label: "Usage", icon: "video" },
    { to: "/admin/users", label: "Users", icon: "users" },
    { to: "/admin/access", label: "Access", icon: "globe" },
    { to: "/admin/plans", label: "Plans", icon: "grid" },
    { to: "/admin/billing", label: "Billing", icon: "file" },
    { to: "/admin/health", label: "Health", icon: "chart" },
    { to: "/admin/announcements", label: "Announcements", icon: "bell" },
    { to: "/admin/admins", label: "Admins", icon: "lock" },
    { to: "/admin/audit", label: "Audit log", icon: "shield" },
  ];
  const home = me.data.organizations.find((o) => !o.suspended);
  return (
    <div className="flex min-h-dvh">
      {/* Eleven places is too many for a bottom bar, so on a phone the list slides in over the page. */}
      {menuOpen && <button type="button" aria-label="Close menu" className="fixed inset-0 z-30 bg-slate-900/40 md:hidden" onClick={() => setMenuOpen(false)} />}
      <aside className={cx(
        "fixed inset-y-0 left-0 z-40 flex w-60 shrink-0 flex-col overflow-y-auto border-r border-slate-200 bg-white p-4 transition-transform md:sticky md:top-0 md:z-auto md:h-dvh md:translate-x-0 dark:border-slate-800 dark:bg-slate-900",
        menuOpen ? "translate-x-0" : "-translate-x-full",
      )}>
        <div className="mb-6 flex items-center gap-2"><Logo /><Badge tone="amber">{check.data?.roleLabel ?? "Admin"}</Badge></div>
        <nav className="space-y-1" aria-label="Platform admin">
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setMenuOpen(false)} className={({ isActive }) => cx("flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium", isActive ? "bg-brand-50 text-brand-700 dark:bg-brand-700/25 dark:text-brand-100" : "text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800")}>
              <Icon name={n.icon} className="h-4 w-4" />{n.label}
            </NavLink>
          ))}
        </nav>
        <div className="mt-auto space-y-1 text-sm">
          {home && <Link to={`/o/${home.slug}`} className="block rounded-lg px-3 py-2 text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800">← Back to {APP_NAME}</Link>}
          <p className="break-all px-3 text-xs text-slate-400">Signed in as {me.data.user.email}. Everything you change here is recorded.</p>
        </div>
      </aside>
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-14 shrink-0 items-center gap-3 border-b border-slate-200 bg-white px-4 md:hidden dark:border-slate-800 dark:bg-slate-900">
          <button type="button" onClick={() => setMenuOpen(true)} aria-label="Open menu" aria-expanded={menuOpen} className="rounded-lg p-2 text-slate-600 [@media(pointer:coarse)]:p-3 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800">
            <Icon name="menu" />
          </button>
          <Logo />
          <Badge tone="amber" className="ml-auto">{check.data?.roleLabel ?? "Admin"}</Badge>
        </header>
        <main className="min-w-0 flex-1 p-4 md:p-8"><div className="mx-auto max-w-6xl"><PlatformContext.Provider value={check.data!}><Outlet /></PlatformContext.Provider></div></main>
      </div>
    </div>
  );
}

export function Header({ title, subtitle, action }: { title: string; subtitle?: ReactNode; action?: ReactNode }) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
      <div><h1 className="text-2xl font-semibold tracking-tight">{title}</h1>{subtitle && <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">{subtitle}</p>}</div>
      {action}
    </div>
  );
}

export function Stat({ label, value, detail }: { label: string; value: ReactNode; detail?: string }) {
  return (
    <Card className="p-5">
      <div className="text-sm text-slate-500">{label}</div>
      <div className="mt-1 text-3xl font-semibold tabular-nums">{value}</div>
      {detail && <div className="mt-1 truncate text-xs text-slate-500">{detail}</div>}
    </Card>
  );
}

const PlanBadge = ({ plan, source }: { plan: string; source: string }) =>
  plan === "free" ? <Badge>Free</Badge> : <Badge tone={source === "override" ? "amber" : "brand"}>Pro{source === "override" ? " (given)" : ""}</Badge>;

// ---------------------------------------------------------------- overview

type Overview = {
  organizations: { total: number; suspended: number; newLast7Days: number };
  users: { total: number; activeLast7Days: number; newLast7Days: number; disabled: number };
  billing: { enabled: boolean; paying: number; pastDue: number; seats: number; proByOverride: number; failedPaymentsLast30Days: number; mrr: { currency: string; amount: number }[] };
  activity: { messagesLast7Days: number; callsLast30Days: number; callMinutesLast30Days: number };
};

export function PlatformOverview() {
  const data = useQuery({ queryKey: ["platform-overview"], queryFn: () => api<Overview>(`${P}/overview`) });
  const access = useAccess().data;
  const o = data.data;
  return (
    <>
      <Header title="Overview" subtitle={`Everything on ${APP_NAME}.`} />
      {!o ? <div className="flex justify-center p-10 text-brand-600"><Spinner /></div> : (
        <div className="space-y-6">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Stat label="Monthly revenue (est.)" value={o.billing.mrr.length ? o.billing.mrr.map((m) => money(m.amount, m.currency)).join(" + ") : "—"} detail={o.billing.enabled ? `${o.billing.paying} paying · ${o.billing.seats} seats` : "Billing isn't switched on"} />
            <Stat label="Organizations" value={o.organizations.total} detail={`${o.organizations.newLast7Days} new this week${o.organizations.suspended ? ` · ${o.organizations.suspended} suspended` : ""}`} />
            <Stat label="Users" value={o.users.total} detail={`${o.users.activeLast7Days} active this week · ${o.users.newLast7Days} new`} />
            <Stat label="Pro given (not billed)" value={o.billing.proByOverride} detail="Demos and agreements" />
          </div>
          {access && (!access.signup.open || access.organizations.limited > 0) && (
            <Alert kind="info">
              {!access.signup.open && "Sign-up is closed: only invited people can create accounts. "}
              {access.organizations.limited > 0 && `${access.organizations.limited} ${access.organizations.limited === 1 ? "organization has" : "organizations have"} limited access. `}
              <Link to="/admin/access" className="font-medium underline">Access</Link>
            </Alert>
          )}
          {(o.billing.pastDue > 0 || o.billing.failedPaymentsLast30Days > 0) && (
            <Alert>{o.billing.pastDue} {o.billing.pastDue === 1 ? "organization is" : "organizations are"} behind on payment; {o.billing.failedPaymentsLast30Days} failed payments in the last 30 days. <Link to="/admin/billing" className="font-medium underline">See billing</Link></Alert>
          )}
          <div className="grid gap-4 sm:grid-cols-3">
            <Stat label="Messages (7 days)" value={o.activity.messagesLast7Days.toLocaleString()} />
            <Stat label="Calls and meetings (30 days)" value={o.activity.callsLast30Days.toLocaleString()} />
            <Stat label="Call minutes (30 days)" value={o.activity.callMinutesLast30Days.toLocaleString()} />
          </div>
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------- organizations

type OrgRow = { id: string; name: string; slug: string; createdAt: string; suspended: boolean; ipLimited: boolean; plan: "free" | "pro"; planSource: string; overrideUntil: string | null; subscriptionStatus: string | null; seats: number; members: number; ownerEmail: string | null };

export function PlatformOrganizations() {
  const [q, setQ] = useState("");
  const [search, setSearch] = useState("");
  const [plan, setPlan] = useState("all");
  const [params] = useSearchParams();
  const [status, setStatus] = useState(params.get("status") ?? "all");
  const list = useInfiniteQuery({
    queryKey: ["platform-orgs", search, plan, status],
    queryFn: ({ pageParam }) => api<{ organizations: OrgRow[]; hasMore: boolean }>(`${P}/organizations?${new URLSearchParams({ q: search, plan, status, page: String(pageParam) })}`),
    initialPageParam: 0, getNextPageParam: (last, all) => (last.hasMore ? all.length : undefined),
  });
  const rows = list.data?.pages.flatMap((p) => p.organizations) ?? [];
  return (
    <>
      <Header title="Organizations" subtitle="Every customer, newest first." />
      <form onSubmit={(e) => { e.preventDefault(); setSearch(q.trim()); }} className="mb-4 flex flex-wrap gap-2">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, address or owner's email" aria-label="Search organizations" className="w-full flex-1 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm sm:min-w-64 dark:border-slate-700 dark:bg-slate-900" />
        <select value={plan} onChange={(e) => setPlan(e.target.value)} aria-label="Plan" className="rounded-lg border border-slate-300 bg-white px-2 py-2 text-sm dark:border-slate-700 dark:bg-slate-900">
          <option value="all">All plans</option><option value="free">Free</option><option value="billing">Pro (paying)</option><option value="override">Pro (given)</option><option value="past_due">Payment overdue</option>
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Status" className="rounded-lg border border-slate-300 bg-white px-2 py-2 text-sm dark:border-slate-700 dark:bg-slate-900">
          <option value="all">Any status</option><option value="active">Active</option><option value="limited">Limited access</option><option value="suspended">Suspended</option>
        </select>
        <Button type="submit" variant="secondary">Search</Button>
      </form>
      <Card className="overflow-x-auto p-0">
        <table className="w-full min-w-[640px] text-left text-sm">
          <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500 dark:border-slate-800">
            <tr><th className="px-4 py-3 font-semibold">Organization</th><th className="px-4 py-3 font-semibold">Owner</th><th className="px-4 py-3 font-semibold">Plan</th><th className="px-4 py-3 text-right font-semibold">Members</th><th className="px-4 py-3 font-semibold">Created</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
            {rows.map((o) => (
              <tr key={o.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                <td className="px-4 py-3"><Link to={`/admin/organizations/${o.id}`} className="font-medium text-brand-700 hover:underline dark:text-brand-100">{o.name}</Link><div className="text-xs text-slate-500">/o/{o.slug}{o.suspended ? <span className="ml-2 font-medium text-red-600">Suspended</span> : o.ipLimited && <span className="ml-2 font-medium text-amber-700 dark:text-amber-400">Limited access</span>}</div></td>
                <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{o.ownerEmail ?? "—"}</td>
                <td className="px-4 py-3"><PlanBadge plan={o.plan} source={o.planSource} />{o.subscriptionStatus === "past_due" && <span className="ml-1"><Badge tone="red">Overdue</Badge></span>}{o.planSource === "billing" && <div className="mt-0.5 text-xs text-slate-500">{o.seats} seats</div>}</td>
                <td className="px-4 py-3 text-right tabular-nums">{o.members}</td>
                <td className="px-4 py-3 text-slate-500">{day(o.createdAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {list.isLoading && <div className="flex justify-center p-8 text-brand-600"><Spinner /></div>}
        {list.data && rows.length === 0 && <p className="p-8 text-center text-sm text-slate-500">No organizations match.</p>}
        {list.hasNextPage && <div className="border-t border-slate-100 p-3 text-center dark:border-slate-800"><button type="button" onClick={() => void list.fetchNextPage()} className="text-sm font-medium text-brand-600 hover:underline">Load more</button></div>}
      </Card>
    </>
  );
}

type OrgDetail = {
  organization: {
    id: string; name: string; slug: string; createdAt: string; suspendedAt: string | null; suspendedReason: string | null;
    accessLimited: boolean; ipAllowlist: string[];
    accessCodes: { id: string; label: string; hint: string; createdAt: string; expiresAt: string; revokedAt: string | null; lastUsedAt: string | null; status: "active" | "expired" | "revoked"; browsers: number; usedBy: string[]; addresses: string[] }[];
  };
  plan: { id: "free" | "pro"; name: string; source: "free" | "billing" | "override"; overrideUntil: string | null; limits: { maxMembers: number; storageBytes: number } };
  subscription: { status: string; plan: string; seats: number; currentPeriodEnd: string | null; cancelAtPeriodEnd: boolean; stripeCustomerUrl: string } | null;
  usage: { members: number; pendingInvitations: number; storageBytes: number; teams: number; messagesLast30Days: number; callsLast30Days: number; meetingsLast30Days: number };
  members: { id: string; name: string; email: string; role: string; status: string; lastSignInAt: string | null; disabled: boolean }[];
  recentActivity: { id: string; at: string; actor: { name: string } | null; summary: string }[];
};

export function PlatformOrganization() {
  const { id = "" } = useParams();
  const qc = useQueryClient();
  const can = useCan();
  const key = ["platform-org", id];
  const data = useQuery({ queryKey: key, queryFn: () => api<OrgDetail>(`${P}/organizations/${id}`) });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [suspendOpen, setSuspendOpen] = useState(false);
  const inMonth = new Date(Date.now() + 30 * 86400_000).toISOString().slice(0, 10);
  const [until, setUntil] = useState(inMonth);
  const planList = useQuery({ queryKey: ["platform-plans"], queryFn: () => api<{ plans: PlanRow[] }>(`${P}/plans`), enabled: can("orgs.plan") });
  const givable = (planList.data?.plans ?? []).filter((p) => !p.isDefault && !p.archived);
  const [givePlan, setGivePlan] = useState("pro");
  async function run(name: string, path: string, body?: unknown) {
    setBusy(name); setError(null);
    try {
      qc.setQueryData(key, await api<OrgDetail>(`${P}/organizations/${id}/${path}`, { method: "POST", body }));
      void qc.invalidateQueries({ queryKey: ["platform-orgs"] });
      return true;
    } catch (err) { setError(errorText(err)); return false; } finally { setBusy(null); }
  }
  const d = data.data;
  if (data.error) return <Alert>{errorText(data.error)}</Alert>;
  if (!d) return <div className="flex justify-center p-10 text-brand-600"><Spinner /></div>;
  const o = d.organization;

  return (
    <>
      <Link to="/admin/organizations" className="text-sm text-brand-600 hover:underline">← Organizations</Link>
      <Header title={o.name} subtitle={<>/o/{o.slug} · created {day(o.createdAt)} {o.suspendedAt && <Badge tone="red">Suspended {day(o.suspendedAt)}</Badge>}</>} />
      {error && <div className="mb-4"><Alert>{error}</Alert></div>}
      {o.suspendedAt && <div className="mb-4"><Alert>Suspended: {o.suspendedReason}. Nobody in it can use {APP_NAME}, and its meeting links don't work.</Alert></div>}

      <OrgAccess key={o.id} org={o} onSaved={(next) => { qc.setQueryData(key, next); void qc.invalidateQueries({ queryKey: ["platform-orgs"] }); void qc.invalidateQueries({ queryKey: ["platform-access"] }); }} />

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <h2 className="font-semibold">Plan</h2>
          <p className="mt-2 flex items-center gap-2 text-2xl font-semibold">{d.plan.name}<PlanBadge plan={d.plan.id} source={d.plan.source} /></p>
          <p className="mt-1 text-sm text-slate-500">{d.plan.source === "override" ? `Given without billing${d.plan.overrideUntil ? ` until ${day(d.plan.overrideUntil)}` : " with no end date"}.` : d.plan.source === "billing" ? "Paid through Stripe." : "Free plan."}</p>
          {can("orgs.plan") && <div className="mt-4 space-y-3 border-t border-slate-100 pt-4 dark:border-slate-800">
            <p className="text-sm font-medium">{d.plan.source === "override" ? "Change the plan or end date" : "Give a plan without billing"}</p>
            <div className="flex flex-wrap items-end gap-2">
              <label className="text-sm font-medium">Plan
                <select value={givePlan} onChange={(e) => setGivePlan(e.target.value)} className="mt-1.5 block rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm dark:border-slate-700 dark:bg-slate-900">
                  {givable.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </label>
              <Field label="Until" type="date" value={until} min={new Date(Date.now() + 86400_000).toISOString().slice(0, 10)} onChange={(e) => setUntil(e.target.value)} className="w-44" />
              <Button onClick={() => run("give", "plan", { plan: givePlan, until })} loading={busy === "give"}>Give {givable.find((p) => p.id === givePlan)?.name ?? "plan"}</Button>
              {d.plan.source === "override" && <Button variant="ghost" className="text-red-600" onClick={() => { if (confirm("Remove the given plan? They go back to their billing plan (Free unless they pay).")) void run("remove", "plan", { plan: "none" }); }} loading={busy === "remove"}>Remove</Button>}
            </div>
          </div>}
        </Card>
        <Card>
          <h2 className="font-semibold">Subscription</h2>
          {d.subscription && d.subscription.status !== "none" ? (
            <dl className="mt-3 grid gap-y-2 text-sm sm:grid-cols-2">
              <dt className="text-slate-500">Status</dt><dd><Badge tone={d.subscription.status === "active" || d.subscription.status === "trialing" ? "brand" : d.subscription.status === "past_due" ? "red" : "slate"}>{d.subscription.status.replace("_", " ")}</Badge></dd>
              <dt className="text-slate-500">Seats</dt><dd>{d.subscription.seats}</dd>
              <dt className="text-slate-500">{d.subscription.cancelAtPeriodEnd ? "Ends" : "Renews"}</dt><dd>{day(d.subscription.currentPeriodEnd)}</dd>
            </dl>
          ) : <p className="mt-3 text-sm text-slate-500">No paid subscription.</p>}
          {d.subscription && <a href={d.subscription.stripeCustomerUrl} target="_blank" rel="noopener noreferrer" className="mt-4 inline-block text-sm font-medium text-brand-600 hover:underline">Open in Stripe (invoices, refunds) ↗</a>}
        </Card>
      </div>

      <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Members" value={`${d.usage.members} / ${d.plan.limits.maxMembers}`} detail={`${d.usage.pendingInvitations} invitations open`} />
        <Stat label="Storage" value={formatSize(d.usage.storageBytes)} detail={`of ${formatSize(d.plan.limits.storageBytes)}`} />
        <Stat label="Messages (30 days)" value={d.usage.messagesLast30Days} detail={`${d.usage.teams} teams`} />
        <Stat label="Calls / meetings (30 days)" value={`${d.usage.callsLast30Days} / ${d.usage.meetingsLast30Days}`} />
      </div>
      <p className="mt-2 text-sm"><Link to={`/admin/usage/${o.id}`} className="font-medium text-brand-600 hover:underline">Calling usage: every meeting and call, who attended, each person's minutes →</Link></p>

      <h2 className="mb-3 mt-8 text-lg font-semibold">Members</h2>
      <Card className="overflow-x-auto p-0">
        <table className="w-full min-w-[640px] text-left text-sm">
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
            {d.members.map((m) => (
              <tr key={m.id}>
                <td className="px-4 py-2.5"><Link to={`/admin/users/${m.id}`} className="font-medium hover:underline">{m.name}</Link><div className="text-xs text-slate-500">{m.email}</div></td>
                <td className="px-4 py-2.5"><Badge tone={m.role === "owner" ? "brand" : "slate"}>{m.role}</Badge>{m.status === "suspended" && <span className="ml-1"><Badge tone="red">suspended</Badge></span>}{m.disabled && <span className="ml-1"><Badge tone="red">account disabled</Badge></span>}</td>
                <td className="px-4 py-2.5 text-right text-xs text-slate-500">last sign-in {m.lastSignInAt ? formatShort(m.lastSignInAt) : "never"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <h2 className="mb-3 mt-8 text-lg font-semibold">Recent activity in the organization</h2>
      <Card className="p-0">
        <ul className="divide-y divide-slate-100 dark:divide-slate-800">
          {d.recentActivity.map((e) => (
            <li key={e.id} className="flex items-center gap-3 px-4 py-2.5 text-sm"><span className="min-w-0 flex-1"><span className="font-medium">{e.actor?.name ?? "System"}</span> {e.summary}</span><span className="shrink-0 text-xs text-slate-400">{formatShort(e.at)}</span></li>
          ))}
          {d.recentActivity.length === 0 && <li className="p-6 text-center text-sm text-slate-500">Nothing yet.</li>}
        </ul>
      </Card>

      {can("orgs.suspend") && <div className="mt-8 rounded-2xl p-5 ring-1 ring-red-200 dark:ring-red-900">
        <h2 className="font-semibold">{o.suspendedAt ? "Reactivate" : "Suspend this organization"}</h2>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">{o.suspendedAt ? "Members can use it again straight away." : "Turns it off: for abuse, unpaid accounts, or a demo nobody should be using. Everyone in it loses access, calls in progress end, and its meeting links stop working, until you reactivate it."}</p>
        {o.suspendedAt
          ? <Button className="mt-3" variant="secondary" onClick={() => run("reactivate", "reactivate")} loading={busy === "reactivate"}>Reactivate</Button>
          : <Button className="mt-3" variant="danger" onClick={() => setSuspendOpen(true)}>Suspend…</Button>}
      </div>}
      <ReasonDialog open={suspendOpen} title={`Suspend ${o.name}`} action="Suspend" busy={busy === "suspend"} onClose={() => setSuspendOpen(false)}
        onSubmit={async (reason) => { if (await run("suspend", "suspend", { reason })) setSuspendOpen(false); }} />
    </>
  );
}

// Where the organization can be used from: anywhere, or (limited) only from a list of IP addresses and ranges and/or
// with an access code given to a client (for testers on networks without a fixed address). Turning it off altogether is
// Suspend, at the bottom of the page.
function OrgAccess({ org, onSaved }: { org: OrgDetail["organization"]; onSaved: (d: OrgDetail) => void }) {
  const can = useCan();
  const mine = useAccess().data?.yourAddress ?? null;
  const [limited, setLimited] = useState(org.accessLimited);
  const [text, setText] = useState(org.ipAllowlist.join("\n"));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const entries = text.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean);
  const changed = limited !== org.accessLimited || (limited && entries.join(",") !== org.ipAllowlist.join(","));
  async function save() {
    setBusy(true); setError(null); setSaved(false);
    try {
      const next = await api<OrgDetail>(`${P}/organizations/${org.id}/access`, { method: "PUT", body: { limited, ipAllowlist: limited ? entries : [] } });
      onSaved(next);
      // As the server tidied it (duplicates gone, addresses written the usual way).
      setText(next.organization.ipAllowlist.join("\n"));
      setLimited(next.organization.accessLimited);
      setSaved(true);
    } catch (err) { setError(errorText(err)); } finally { setBusy(false); }
  }
  const active = org.accessCodes.filter((c) => c.status === "active").length;
  const ways = [
    org.ipAllowlist.length ? `from ${org.ipAllowlist.length === 1 ? "one address" : `${org.ipAllowlist.length} addresses`} (${org.ipAllowlist.join(", ")})` : null,
    active ? `with ${active === 1 ? "an access code" : `one of ${active} access codes`}` : null,
  ].filter(Boolean);
  const now = org.suspendedAt ? "Off: suspended, so nobody can use it from anywhere (see the bottom of this page)."
    : !org.accessLimited ? "Open: its members can use it from anywhere."
    : ways.length ? `Limited: only ${ways.join(", or ")}.`
    : "Limited, with no addresses or active codes yet: only super admins can use it.";
  return (
    <Card className="mb-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-semibold">Access</h2>
        {!org.suspendedAt && org.accessLimited && <Badge tone="amber">Limited access</Badge>}
      </div>
      <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">{now}</p>
      {can("orgs.access") && (
        <div className="mt-4 space-y-3 border-t border-slate-100 pt-4 dark:border-slate-800">
          <fieldset className="space-y-1.5 text-sm">
            <legend className="mb-1 font-medium">Where it can be used from</legend>
            <label className="flex items-center gap-2"><input type="radio" name={`access-${org.id}`} checked={!limited} onChange={() => { setLimited(false); setSaved(false); }} className="h-4 w-4" />Anywhere</label>
            <label className="flex items-center gap-2"><input type="radio" name={`access-${org.id}`} checked={limited} onChange={() => { setLimited(true); setSaved(false); }} className="h-4 w-4" />Only from these IP addresses, or with an access code</label>
          </fieldset>
          {limited && (
            <div>
              <label htmlFor={`addresses-${org.id}`} className="mb-1.5 block text-sm font-medium">IP addresses <span className="font-normal text-slate-500">(optional when you give access codes)</span></label>
              <textarea id={`addresses-${org.id}`} rows={3} value={text} onChange={(e) => { setText(e.target.value); setSaved(false); }} spellCheck={false}
                placeholder={"203.0.113.7\n198.51.100.0/24"} className="block w-full max-w-md rounded-lg border border-slate-300 bg-white px-3 py-2 font-mono text-sm dark:border-slate-700 dark:bg-slate-900" />
              <p className="mt-1 text-xs text-slate-500">One per line: an address (203.0.113.7) or a range (203.0.113.0/24). IPv6 works too.</p>
              {mine && !entries.includes(mine) && (
                <button type="button" onClick={() => { setText((t) => `${t.trim() ? `${t.trim()}\n` : ""}${mine}`); setSaved(false); }} className="mt-2 text-sm font-medium text-brand-600 hover:underline">
                  + Add my address ({mine})
                </button>
              )}
            </div>
          )}
          <p className="text-xs text-slate-500">From anywhere else, its members can still sign in but can't open it or its meeting links; pages already open close within a minute. Calls already in progress carry on. Super admins can always open it, from anywhere.</p>
          {error && <Alert>{error}</Alert>}
          {saved && !changed && <Alert kind="success">Saved.</Alert>}
          <Button onClick={save} loading={busy} disabled={!changed}>Save access</Button>
          {limited && <AccessCodes org={org} onSaved={onSaved} />}
        </div>
      )}
    </Card>
  );
}

type AccessCode = OrgDetail["organization"]["accessCodes"][number];
const CODE_DAYS = [1, 3, 7, 14, 30, 90];

// Codes for clients who can't give a fixed address: each opens the organization, from any network, in the browser it's
// entered in, until it ends; while one is active the organization's meeting links open for guests anywhere.
function AccessCodes({ org, onSaved }: { org: OrgDetail["organization"]; onSaved: (d: OrgDetail) => void }) {
  const [label, setLabel] = useState("");
  const [days, setDays] = useState(7);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [made, setMade] = useState<{ code: string; label: string } | null>(null);
  const [copied, setCopied] = useState(false);
  async function create(e: FormEvent) {
    e.preventDefault();
    setBusy("create"); setError(null); setMade(null); setCopied(false);
    try {
      const { code, ...detail } = await api<OrgDetail & { code: string }>(`${P}/organizations/${org.id}/access-codes`, { method: "POST", body: { label: label.trim(), days } });
      onSaved(detail);
      setMade({ code, label: label.trim() });
      setLabel("");
    } catch (err) { setError(errorText(err)); } finally { setBusy(null); }
  }
  async function revoke(c: AccessCode) {
    if (!confirm(`Revoke the code "${c.label}"? Everyone it let in loses access within a minute.`)) return;
    setBusy(c.id); setError(null);
    try { onSaved(await api<OrgDetail>(`${P}/organizations/${org.id}/access-codes/${c.id}/revoke`, { method: "POST" })); }
    catch (err) { setError(errorText(err)); } finally { setBusy(null); }
  }
  async function copy(code: string) {
    try { await navigator.clipboard.writeText(code); setCopied(true); } catch { /* the code is on screen to copy by hand */ }
  }
  return (
    <div className="space-y-3 border-t border-slate-100 pt-4 dark:border-slate-800">
      <div>
        <h3 className="text-sm font-medium">Access codes</h3>
        <p className="mt-1 text-xs text-slate-500">For clients who can't give a fixed address. Anyone in the organization who enters a code can use it from any network, in that browser, until the code ends. While a code is active, the organization's meeting links open for guests anywhere (the waiting room still applies).</p>
      </div>
      {made && (
        <Alert kind="success">
          <span className="block">Access code for {made.label}:</span>
          <span className="mt-1 flex flex-wrap items-center gap-2"><code className="rounded bg-white px-2 py-1 font-mono text-base font-semibold tracking-wider text-slate-900 ring-1 ring-emerald-200 dark:bg-slate-900 dark:text-slate-100" data-access-code>{made.code}</code>
            <button type="button" onClick={() => void copy(made.code)} className="text-sm font-medium underline">{copied ? "Copied" : "Copy"}</button></span>
          <span className="mt-1 block text-xs">Send it to the client. It isn't shown again; you can always make a new one.</span>
        </Alert>
      )}
      <form onSubmit={create} className="flex flex-wrap items-end gap-2">
        <Field label="Who it's for" value={label} onChange={(e) => setLabel(e.target.value)} maxLength={80} placeholder="e.g. Acme pilot" className="w-56" required />
        <label className="text-sm font-medium">Valid for
          <select value={days} onChange={(e) => setDays(Number(e.target.value))} aria-label="Valid for" className="mt-1.5 block rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm dark:border-slate-700 dark:bg-slate-900">
            {CODE_DAYS.map((d) => <option key={d} value={d}>{d === 1 ? "1 day" : `${d} days`}</option>)}
          </select>
        </label>
        <Button type="submit" variant="secondary" loading={busy === "create"} disabled={!label.trim()}>Create code</Button>
      </form>
      {error && <Alert>{error}</Alert>}
      {org.accessCodes.length > 0 && (
        <ul className="divide-y divide-slate-100 rounded-lg ring-1 ring-slate-200 dark:divide-slate-800 dark:ring-slate-700">
          {org.accessCodes.map((c) => (
            <li key={c.id} className="flex flex-wrap items-start gap-3 px-3 py-2.5 text-sm">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">{c.label}</span>
                  <span className="font-mono text-xs text-slate-500">…{c.hint}</span>
                  {c.status === "active" ? <Badge tone="brand">Until {day(c.expiresAt)}</Badge> : <Badge tone={c.status === "revoked" ? "red" : "slate"}>{c.status === "revoked" ? "Revoked" : "Expired"}</Badge>}
                </div>
                <div className="mt-0.5 break-words text-xs text-slate-500">
                  {c.browsers === 0 ? "Not used yet." : <>
                    Used by {c.usedBy.join(", ") || "someone"} · {c.browsers === 1 ? "1 browser" : `${c.browsers} browsers`}{c.lastUsedAt && ` · last used ${formatShort(c.lastUsedAt)}`}
                    {c.addresses.length > 0 && <> · from {c.addresses.join(", ")}</>}
                  </>}
                </div>
              </div>
              {c.status === "active" && <Button variant="ghost" className="text-red-600" onClick={() => void revoke(c)} loading={busy === c.id}>Revoke</Button>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ReasonDialog({ open, title, action, busy, onClose, onSubmit }: { open: boolean; title: string; action: string; busy: boolean; onClose: () => void; onSubmit: (reason: string) => void }) {
  const [reason, setReason] = useState("");
  return (
    <Dialog open={open} onClose={onClose} title={title}>
      <form onSubmit={(e: FormEvent) => { e.preventDefault(); onSubmit(reason); }} className="space-y-4">
        <Field label="Reason (recorded in the audit log)" value={reason} onChange={(e) => setReason(e.target.value)} minLength={3} maxLength={300} required autoFocus />
        <div className="flex justify-end gap-2"><Button type="button" variant="secondary" onClick={onClose}>Cancel</Button><Button type="submit" variant="danger" loading={busy}>{action}</Button></div>
      </form>
    </Dialog>
  );
}

// ---------------------------------------------------------------- access

type AccessSettings = { signup: { open: boolean; message: string | null }; yourAddress: string | null; organizations: { suspended: number; limited: number } };
const useAccess = () => useQuery({ queryKey: ["platform-access"], queryFn: () => api<AccessSettings>(`${P}/access`) });

export function PlatformAccess() {
  const data = useAccess();
  const d = data.data;
  if (data.error) return <Alert>{errorText(data.error)}</Alert>;
  return (
    <>
      <Header title="Access" subtitle={`Who can sign up for ${APP_NAME}, and where organizations can be used from.`} />
      {!d ? <div className="flex justify-center p-10 text-brand-600"><Spinner /></div> : (
        <div className="grid items-start gap-4 lg:grid-cols-2">
          <SignupCard key={JSON.stringify(d.signup)} signup={d.signup} />
          <Card>
            <h2 className="font-semibold">Organizations</h2>
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">Limit an organization to certain IP addresses or access codes, or turn it off (suspend it), from its page under Organizations.</p>
            <dl className="mt-4 grid grid-cols-[1fr_auto] gap-y-2 text-sm">
              <dt><Link to="/admin/organizations?status=limited" className="text-brand-600 hover:underline">Limited (addresses or access codes)</Link></dt><dd className="text-right tabular-nums">{d.organizations.limited}</dd>
              <dt><Link to="/admin/organizations?status=suspended" className="text-brand-600 hover:underline">Suspended (off)</Link></dt><dd className="text-right tabular-nums">{d.organizations.suspended}</dd>
            </dl>
            <div className="mt-4 border-t border-slate-100 pt-4 text-sm dark:border-slate-800">
              <p className="font-medium">Your address</p>
              <p className="mt-1 font-mono">{d.yourAddress ?? "Unknown"}</p>
              <p className="mt-1 text-xs text-slate-500">The address this panel sees you coming from. Home and mobile addresses can change from day to day.</p>
              <p className="mt-2 text-xs text-slate-500">Super admins aren't held to organizations' address limits: they can open every organization, and this panel, from anywhere.</p>
            </div>
          </Card>
        </div>
      )}
    </>
  );
}

function SignupCard({ signup }: { signup: AccessSettings["signup"] }) {
  const can = useCan();
  const qc = useQueryClient();
  const [open, setOpen] = useState(signup.open);
  const [message, setMessage] = useState(signup.message ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const changed = open !== signup.open || message.trim() !== (signup.message ?? "");
  async function save() {
    setBusy(true); setError(null);
    try {
      qc.setQueryData(["platform-access"], await api<AccessSettings>(`${P}/access/signup`, { method: "PUT", body: { open, message: message.trim() || null } }));
      void qc.invalidateQueries({ queryKey: ["providers"] });
    } catch (err) { setError(errorText(err)); } finally { setBusy(false); }
  }
  return (
    <Card>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-semibold">Sign-up</h2>
        <Badge tone={signup.open ? "brand" : "amber"}>{signup.open ? "Open" : "Closed"}</Badge>
      </div>
      <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
        {signup.open ? "Anyone can create an account and start an organization." : "Only people invited to an organization can create an account. Only platform admins can start new organizations."}
      </p>
      {can("signup.manage") && (
        <div className="mt-4 space-y-3 border-t border-slate-100 pt-4 dark:border-slate-800">
          <fieldset className="space-y-1.5 text-sm">
            <legend className="sr-only">Sign-up</legend>
            <label className="flex items-start gap-2"><input type="radio" name="signup" checked={open} onChange={() => setOpen(true)} className="mt-0.5 h-4 w-4" /><span><span className="font-medium">Open</span>: anyone can sign up</span></label>
            <label className="flex items-start gap-2"><input type="radio" name="signup" checked={!open} onChange={() => setOpen(false)} className="mt-0.5 h-4 w-4" /><span><span className="font-medium">Closed</span>: invitations only. Everyone who already has an account signs in as usual.</span></label>
          </fieldset>
          {!open && (
            <div>
              <label htmlFor="signup-message" className="mb-1.5 block text-sm font-medium">Message on the sign-up page (optional)</label>
              <textarea id="signup-message" rows={2} maxLength={300} value={message} onChange={(e) => setMessage(e.target.value)}
                placeholder="We're in private beta. Write to hello@example.com for a demo."
                className="block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900" />
            </div>
          )}
          {error && <Alert>{error}</Alert>}
          <Button onClick={save} loading={busy} disabled={!changed}>Save sign-up</Button>
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------- users

type UserRow = { id: string; name: string; email: string; createdAt: string; lastSignInAt: string | null; emailVerified: boolean; disabled: boolean; platformRole: Role | null; mfaEnabled: boolean; organizations: number };

export function PlatformUsers() {
  const [q, setQ] = useState("");
  const [search, setSearch] = useState("");
  const list = useInfiniteQuery({
    queryKey: ["platform-users", search],
    queryFn: ({ pageParam }) => api<{ users: UserRow[]; hasMore: boolean }>(`${P}/users?${new URLSearchParams({ q: search, page: String(pageParam) })}`),
    initialPageParam: 0, getNextPageParam: (last, all) => (last.hasMore ? all.length : undefined),
  });
  const rows = list.data?.pages.flatMap((p) => p.users) ?? [];
  return (
    <>
      <Header title="Users" subtitle="Everyone with an account, newest first." />
      <form onSubmit={(e) => { e.preventDefault(); setSearch(q.trim()); }} className="mb-4 flex gap-2">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Email or name" aria-label="Search users" className="w-full flex-1 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm sm:min-w-64 dark:border-slate-700 dark:bg-slate-900" />
        <Button type="submit" variant="secondary">Search</Button>
      </form>
      <Card className="p-0">
        <ul className="divide-y divide-slate-100 dark:divide-slate-800">
          {rows.map((u) => (
            <li key={u.id}>
              <Link to={`/admin/users/${u.id}`} className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-800/50">
                <Avatar name={u.name} size="sm" />
                <span className="min-w-0 flex-1"><span className="block truncate text-sm font-medium">{u.name}</span><span className="block truncate text-xs text-slate-500">{u.email}</span></span>
                {u.platformRole && <Badge tone="amber">{ROLE_LABEL[u.platformRole]}</Badge>}
                {u.disabled && <Badge tone="red">Disabled</Badge>}
                {!u.emailVerified && <Badge>Unconfirmed</Badge>}
                <span className="w-24 text-right text-xs text-slate-500">{u.organizations} {u.organizations === 1 ? "org" : "orgs"}</span>
                <span className="hidden w-28 text-right text-xs text-slate-400 sm:block">{u.lastSignInAt ? formatShort(u.lastSignInAt) : "never signed in"}</span>
              </Link>
            </li>
          ))}
        </ul>
        {list.isLoading && <div className="flex justify-center p-8 text-brand-600"><Spinner /></div>}
        {list.data && rows.length === 0 && <p className="p-8 text-center text-sm text-slate-500">No users match.</p>}
        {list.hasNextPage && <div className="border-t border-slate-100 p-3 text-center dark:border-slate-800"><button type="button" onClick={() => void list.fetchNextPage()} className="text-sm font-medium text-brand-600 hover:underline">Load more</button></div>}
      </Card>
    </>
  );
}

type UserDetail = {
  user: { id: string; name: string; email: string; createdAt: string; lastSignInAt: string | null; emailVerified: boolean; hasPassword: boolean; disabled: boolean; disabledAt: string | null; platformRole: Role | null; mfaEnabled: boolean };
  organizations: { id: string; name: string; slug: string; role: string; status: string; suspended: boolean }[];
  signInMethods: { provider: string; email: string | null; lastUsedAt: string | null }[];
  activeSessions: number;
};

export function PlatformUser() {
  const { id = "" } = useParams();
  const qc = useQueryClient();
  const can = useCan();
  const me = useMe();
  const key = ["platform-user", id];
  const data = useQuery({ queryKey: key, queryFn: () => api<UserDetail>(`${P}/users/${id}`) });
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [disableOpen, setDisableOpen] = useState(false);
  async function run(name: string, path: string, body?: unknown, message?: string) {
    setBusy(name); setError(null); setDone(null);
    try {
      const r = await api<UserDetail | { ok: boolean }>(`${P}/users/${id}/${path}`, { method: "POST", body });
      if ("user" in r) qc.setQueryData(key, r);
      if (message) setDone(message);
      void qc.invalidateQueries({ queryKey: ["platform-users"] });
      return true;
    } catch (err) { setError(errorText(err)); return false; } finally { setBusy(null); }
  }
  const d = data.data;
  if (data.error) return <Alert>{errorText(data.error)}</Alert>;
  if (!d) return <div className="flex justify-center p-10 text-brand-600"><Spinner /></div>;
  const u = d.user;
  const self = u.id === me.data?.user.id;
  return (
    <>
      <Link to="/admin/users" className="text-sm text-brand-600 hover:underline">← Users</Link>
      <Header title={u.name} subtitle={<>{u.email} · joined {day(u.createdAt)} {u.platformRole && <Badge tone="amber">{ROLE_LABEL[u.platformRole]}</Badge>} {u.disabled && <Badge tone="red">Disabled</Badge>}</>} />
      {error && <div className="mb-4"><Alert>{error}</Alert></div>}
      {done && <div className="mb-4"><Alert kind="success">{done}</Alert></div>}
      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <h2 className="font-semibold">Account</h2>
          <dl className="mt-3 grid gap-y-2 text-sm sm:grid-cols-2">
            <dt className="text-slate-500">Email confirmed</dt><dd>{u.emailVerified ? "Yes" : "No"}</dd>
            <dt className="text-slate-500">Password</dt><dd>{u.hasPassword ? "Set" : "None (email links / Google / Microsoft)"}</dd>
            <dt className="text-slate-500">Sign-in with</dt><dd>{d.signInMethods.length ? d.signInMethods.map((m) => m.provider).join(", ") : "—"}</dd>
            <dt className="text-slate-500">Last sign-in</dt><dd>{u.lastSignInAt ? new Date(u.lastSignInAt).toLocaleString() : "Never"}</dd>
            <dt className="text-slate-500">Signed in on</dt><dd>{d.activeSessions} {d.activeSessions === 1 ? "device" : "devices"}</dd>
          </dl>
        </Card>
        <Card>
          <h2 className="font-semibold">Organizations</h2>
          <ul className="mt-3 space-y-2">
            {d.organizations.map((o) => (
              <li key={o.id} className="flex items-center gap-2 text-sm"><Link to={`/admin/organizations/${o.id}`} className="font-medium hover:underline">{o.name}</Link><Badge>{o.role}</Badge>{o.suspended && <Badge tone="red">suspended</Badge>}</li>
            ))}
            {d.organizations.length === 0 && <li className="text-sm text-slate-500">None.</li>}
          </ul>
        </Card>
      </div>
      {!self && (
        <Card className="mt-4">
          <h2 className="font-semibold">Actions</h2>
          {u.platformRole ? <p className="mt-2 text-sm text-slate-500">{u.name} is a platform admin ({ROLE_LABEL[u.platformRole]}). Change admins on the <Link to="/admin/admins" className="text-brand-600 hover:underline">Admins</Link> page.</p> : (
            <div className="mt-3 flex flex-wrap gap-2">
              {can("users.passwordReset") && <Button variant="secondary" onClick={() => run("reset", "password-reset", undefined, "Password reset email sent.")} loading={busy === "reset"} disabled={u.disabled}>Send password reset</Button>}
              {can("users.signOut") && <Button variant="secondary" onClick={() => { if (confirm(`Sign ${u.name} out on every device?`)) void run("signout", "sign-out", undefined, "Signed out everywhere."); }} loading={busy === "signout"}>Sign out everywhere</Button>}
              {can("users.disable") && (u.disabled
                ? <Button variant="secondary" onClick={() => run("enable", "enable", undefined, "Account enabled.")} loading={busy === "enable"}>Enable account</Button>
                : <Button variant="danger" onClick={() => setDisableOpen(true)}>Disable account…</Button>)}
            </div>
          )}
        </Card>
      )}
      <ReasonDialog open={disableOpen} title={`Disable ${u.name}'s account`} action="Disable" busy={busy === "disable"} onClose={() => setDisableOpen(false)}
        onSubmit={async (reason) => { if (await run("disable", "disable", { reason }, "Account disabled and signed out everywhere.")) setDisableOpen(false); }} />
    </>
  );
}

// ---------------------------------------------------------------- billing activity and the audit log

type BillingActivity = {
  pastDue: { orgId: string; name: string; seats: number; periodEnd: string | null }[];
  failedPayments: { at: string; orgId: string; name: string }[];
  changes: { at: string; orgId: string; name: string; details: Record<string, unknown> }[];
  stripeEvents: { id: string; type: string; receivedAt: string }[];
};

export function PlatformBilling() {
  const data = useQuery({ queryKey: ["platform-billing"], queryFn: () => api<BillingActivity>(`${P}/billing`) });
  const d = data.data;
  const describe = (x: Record<string, unknown>) => x.plan !== undefined && x.status !== undefined ? `${String(x.plan)} · ${String(x.status).replace("_", " ")}` : x.plan === null ? "given plan removed" : x.plan ? `given ${String(x.plan)}${x.until ? ` until ${String(x.until).slice(0, 10)}` : ""}` : x.interval ? `started checkout (${String(x.interval)}ly)` : "";
  return (
    <>
      <Header title="Billing" subtitle={<>What Stripe has told us. Invoices, refunds and card details are in the <a href="https://dashboard.stripe.com" target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:underline">Stripe dashboard ↗</a>.</>} />
      {!d ? <div className="flex justify-center p-10 text-brand-600"><Spinner /></div> : (
        <div className="space-y-8">
          <Section title="Payment overdue" empty="No organization is behind on payment." items={d.pastDue.map((p) => ({ key: p.orgId, left: <Link to={`/admin/organizations/${p.orgId}`} className="font-medium hover:underline">{p.name}</Link>, right: `${p.seats} seats` }))} />
          <Section title="Failed payments (90 days)" empty="None." items={d.failedPayments.map((p, i) => ({ key: `${p.orgId}-${i}`, left: <Link to={`/admin/organizations/${p.orgId}`} className="font-medium hover:underline">{p.name}</Link>, right: formatShort(p.at) }))} />
          <Section title="Plan changes" empty="None yet." items={d.changes.map((c, i) => ({ key: `${c.orgId}-${i}`, left: <><Link to={`/admin/organizations/${c.orgId}`} className="font-medium hover:underline">{c.name}</Link> <span className="text-slate-500">{describe(c.details)}</span></>, right: formatShort(c.at) }))} />
          <Section title="Latest Stripe events" empty="None received." items={d.stripeEvents.map((e) => ({ key: e.id, left: <span className="font-mono text-xs">{e.type}</span>, right: formatShort(e.receivedAt) }))} />
        </div>
      )}
    </>
  );
}

function Section({ title, empty, items }: { title: string; empty: string; items: { key: string; left: ReactNode; right: ReactNode }[] }) {
  return (
    <section>
      <h2 className="mb-2 font-semibold">{title}</h2>
      <Card className="p-0">
        <ul className="divide-y divide-slate-100 dark:divide-slate-800">
          {items.map((i) => <li key={i.key} className="flex items-center gap-3 px-4 py-2.5 text-sm"><span className="min-w-0 flex-1">{i.left}</span><span className="shrink-0 text-xs text-slate-400">{i.right}</span></li>)}
          {items.length === 0 && <li className="px-4 py-4 text-sm text-slate-500">{empty}</li>}
        </ul>
      </Card>
    </section>
  );
}

type AuditPage = { entries: { id: string; at: string; action: string; ip: string | null; targetType: string; targetId: string | null; actor: string; summary: string }[]; hasMore: boolean };

export function PlatformAudit() {
  const log = useInfiniteQuery({
    queryKey: ["platform-audit"],
    queryFn: ({ pageParam }) => api<AuditPage>(`${P}/audit${pageParam ? `?before=${encodeURIComponent(pageParam)}` : ""}`),
    initialPageParam: undefined as string | undefined, getNextPageParam: (last) => (last.hasMore ? last.entries.at(-1)?.at : undefined),
  });
  const rows = log.data?.pages.flatMap((p) => p.entries) ?? [];
  const link = (e: AuditPage["entries"][number]) => (e.targetId && e.targetType !== "platform" ? `/admin/${e.targetType === "organization" ? "organizations" : "users"}/${e.targetId}` : null);
  return (
    <>
      <Header title="Audit log" subtitle="Everything done in this panel and with the server admin tool." />
      <Card className="p-0">
        <ul className="divide-y divide-slate-100 dark:divide-slate-800">
          {rows.map((e) => {
            const to = link(e);
            return (
              <li key={e.id} className="flex items-center gap-3 px-4 py-2.5 text-sm">
                <span className="min-w-0 flex-1"><span className="font-medium">{e.actor}</span> {to ? <Link to={to} className="hover:underline">{e.summary}</Link> : e.summary}</span>
                {e.ip && <span className="hidden font-mono text-xs text-slate-400 sm:block">{e.ip}</span>}
                <span className="w-32 shrink-0 text-right text-xs text-slate-400" title={new Date(e.at).toLocaleString()}>{formatShort(e.at)}</span>
              </li>
            );
          })}
          {log.data && rows.length === 0 && <li className="p-6 text-center text-sm text-slate-500">Nothing yet.</li>}
        </ul>
        {log.isLoading && <div className="flex justify-center p-8 text-brand-600"><Spinner /></div>}
        {log.hasNextPage && <div className="border-t border-slate-100 p-3 text-center dark:border-slate-800"><button type="button" onClick={() => void log.fetchNextPage()} className="text-sm font-medium text-brand-600 hover:underline">Load more</button></div>}
      </Card>
    </>
  );
}

// ---------------------------------------------------------------- admins

type AdminRow = { id: string; name: string; email: string; role: Role; mfaEnabled: boolean; lastSignInAt: string | null; disabled: boolean };
const ROLE_HELP: Record<Role, string> = {
  super: "Everything, including other admins",
  support: "Look at everything; sign people out and send password resets",
  billing: "Look at everything; give and change plans",
};

export function PlatformAdmins() {
  const can = useCan();
  const me = useMe();
  const qc = useQueryClient();
  const data = useQuery({ queryKey: ["platform-admins"], queryFn: () => api<{ admins: AdminRow[] }>(`${P}/admins`) });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const manage = can("admins.manage");
  async function run(key: string, fn: () => Promise<{ admins: AdminRow[] }>) {
    setBusy(key); setError(null);
    try { qc.setQueryData(["platform-admins"], await fn()); } catch (err) { setError(errorText(err)); } finally { setBusy(null); }
  }
  function add(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const d = new FormData(form);
    void run("add", () => api(`${P}/admins`, { method: "POST", body: { email: String(d.get("email")), role: String(d.get("role")) } })).then(() => form.reset());
  }
  return (
    <>
      <Header title="Admins" subtitle="People who can open this panel. Each one needs two-factor sign-in." />
      {error && <div className="mb-4"><Alert>{error}</Alert></div>}
      <Card className="p-0">
        {data.isLoading ? <div className="flex justify-center p-8 text-brand-600"><Spinner /></div> : (
          <ul className="divide-y divide-slate-100 dark:divide-slate-800">
            {data.data?.admins.map((a) => (
              <li key={a.id} className="flex flex-wrap items-center gap-3 px-5 py-3">
                <Avatar name={a.name} size="sm" />
                <div className="min-w-0 flex-1">
                  <Link to={`/admin/users/${a.id}`} className="font-medium hover:underline">{a.name}</Link>{a.id === me.data?.user.id && <span className="text-slate-500"> (you)</span>}
                  <div className="truncate text-sm text-slate-500">{a.email} · last sign-in {day(a.lastSignInAt)}</div>
                </div>
                {!a.mfaEnabled && <Badge tone="red">No two-factor yet</Badge>}
                {manage && a.id !== me.data?.user.id ? (
                  <>
                    <select aria-label={`Role for ${a.name}`} value={a.role} disabled={busy !== null}
                      onChange={(e) => void run(`role-${a.id}`, () => api(`${P}/admins`, { method: "POST", body: { email: a.email, role: e.target.value } }))}
                      className="rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-900">
                      {(Object.keys(ROLE_LABEL) as Role[]).map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
                    </select>
                    <Button size="sm" variant="ghost" className="text-red-600" loading={busy === `remove-${a.id}`}
                      onClick={() => { if (confirm(`Remove ${a.name}'s admin role? They'll be signed out everywhere.`)) void run(`remove-${a.id}`, () => api(`${P}/admins/${a.id}`, { method: "DELETE" })); }}>Remove</Button>
                  </>
                ) : <Badge tone="amber">{ROLE_LABEL[a.role]}</Badge>}
              </li>
            ))}
          </ul>
        )}
      </Card>

      {manage && (
        <Card className="mt-6">
          <h2 className="font-semibold">Add an admin</h2>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">They need an account with a confirmed email. They'll be asked to turn on two-factor sign-in the first time they open the panel.</p>
          <form onSubmit={add} className="mt-4 flex flex-wrap items-end gap-3">
            <Field label="Email" name="email" type="email" required className="min-w-64 flex-1" />
            <label className="text-sm font-medium">Role
              <select name="role" defaultValue="support" className="mt-1.5 block rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm dark:border-slate-700 dark:bg-slate-900">
                {(Object.keys(ROLE_LABEL) as Role[]).map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
              </select>
            </label>
            <Button type="submit" loading={busy === "add"}>Add</Button>
          </form>
          <ul className="mt-4 space-y-1 text-sm text-slate-600 dark:text-slate-400">
            {(Object.keys(ROLE_LABEL) as Role[]).map((r) => <li key={r}><span className="font-medium text-slate-800 dark:text-slate-200">{ROLE_LABEL[r]}:</span> {ROLE_HELP[r]}</li>)}
          </ul>
        </Card>
      )}
    </>
  );
}

// ---------------------------------------------------------------- announcements

type AnnouncementRow = {
  id: string; message: string; level: "info" | "warning" | "critical"; linkUrl: string | null; linkText: string | null; endsAt: string | null;
  orgs: { id: string; name: string }[] | null; showOnSignIn: boolean; startsAt: string; createdAt: string; state: "live" | "scheduled" | "ended";
};
const LEVEL_LABEL = { info: "Info", warning: "Warning", critical: "Critical" };
const localInput = (d: Date) => new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);

export function PlatformAnnouncements() {
  const can = useCan();
  const qc = useQueryClient();
  const data = useQuery({ queryKey: ["platform-announcements"], queryFn: () => api<{ announcements: AnnouncementRow[] }>(`${P}/announcements`) });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [everyone, setEveryone] = useState(true);
  const [picked, setPicked] = useState<{ id: string; name: string }[]>([]);
  const [search, setSearch] = useState("");
  const found = useQuery({ queryKey: ["platform-org-search", search], queryFn: () => api<{ organizations: { id: string; name: string; slug: string }[] }>(`${P}/organizations?q=${encodeURIComponent(search)}`), enabled: !everyone && search.trim().length > 1 });
  const manage = can("announcements.manage");
  async function run(key: string, fn: () => Promise<{ announcements: AnnouncementRow[] }>) {
    setBusy(key); setError(null); setFields({});
    try { qc.setQueryData(["platform-announcements"], await fn()); return true; } catch (err) {
      setError(errorText(err)); if (err instanceof ApiError) setFields(err.fields); return false;
    } finally { setBusy(null); }
  }
  async function create(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const d = new FormData(form);
    const at = (name: string) => (d.get(name) ? new Date(String(d.get(name))).toISOString() : null);
    const ok = await run("create", () => api(`${P}/announcements`, { method: "POST", body: {
      message: String(d.get("message")), level: String(d.get("level")), linkUrl: String(d.get("linkUrl") || "") || null, linkText: String(d.get("linkText") || "") || null,
      orgIds: everyone ? null : picked.map((o) => o.id), showOnSignIn: everyone && d.get("showOnSignIn") === "on", startsAt: at("startsAt"), endsAt: at("endsAt"),
    } }));
    if (ok) { form.reset(); setPicked([]); }
  }
  const tone = (s: AnnouncementRow["state"]) => (s === "live" ? "brand" : s === "scheduled" ? "amber" : "slate") as "brand" | "amber" | "slate";
  return (
    <>
      <Header title="Announcements" subtitle="A banner for everyone, or for chosen organizations. Use it for maintenance, outages and news." />
      {error && <div className="mb-4"><Alert>{error}</Alert></div>}
      {manage && (
        <Card className="mb-6">
          <h2 className="font-semibold">New announcement</h2>
          <form onSubmit={create} className="mt-4 space-y-4">
            <div>
              <label htmlFor="ann-message" className="mb-1.5 block text-sm font-medium">Message</label>
              <textarea id="ann-message" name="message" rows={2} maxLength={500} required placeholder="We'll update the service on Saturday 22:00-22:30 IST; calls may drop briefly."
                className="block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900" />
              {fields.message && <p className="mt-1 text-sm text-red-600">{fields.message}</p>}
            </div>
            <div className="flex flex-wrap gap-3">
              <label className="text-sm font-medium">Level
                <select name="level" defaultValue="info" className="mt-1.5 block rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900">
                  <option value="info">Info (can be dismissed)</option><option value="warning">Warning (can be dismissed)</option><option value="critical">Critical (stays)</option>
                </select>
              </label>
              <Field label="Link (optional)" name="linkUrl" placeholder="https://status.example.com" error={fields.linkUrl} className="min-w-60 flex-1" />
              <Field label="Link text" name="linkText" placeholder="Details" className="w-40" />
            </div>
            <fieldset className="text-sm">
              <legend className="font-medium">Who sees it</legend>
              <label className="mt-1 flex items-center gap-2"><input type="radio" checked={everyone} onChange={() => setEveryone(true)} className="h-4 w-4" />Everyone</label>
              <label className="mt-1 flex items-center gap-2"><input type="radio" checked={!everyone} onChange={() => setEveryone(false)} className="h-4 w-4" />Chosen organizations</label>
              {!everyone && (
                <div className="ml-6 mt-2 space-y-2">
                  <div className="flex flex-wrap gap-1">{picked.map((o) => <button key={o.id} type="button" onClick={() => setPicked((p) => p.filter((x) => x.id !== o.id))} className="rounded-full bg-brand-50 px-2.5 py-1 text-xs text-brand-800 dark:bg-brand-700/30 dark:text-brand-100">{o.name} ×</button>)}</div>
                  <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search organizations…" aria-label="Search organizations" className="w-full max-w-sm rounded-lg border border-slate-300 px-3 py-2 dark:border-slate-700 dark:bg-slate-900" />
                  {found.data && <ul className="max-w-sm rounded-lg ring-1 ring-slate-200 dark:ring-slate-700">{found.data.organizations.filter((o) => !picked.some((p) => p.id === o.id)).slice(0, 8).map((o) => (
                    <li key={o.id}><button type="button" onClick={() => setPicked((p) => [...p, { id: o.id, name: o.name }])} className="w-full px-3 py-1.5 text-left hover:bg-slate-100 dark:hover:bg-slate-800">{o.name} <span className="text-slate-500">/{o.slug}</span></button></li>
                  ))}</ul>}
                </div>
              )}
              {everyone && <label className="ml-6 mt-2 flex items-center gap-2"><input type="checkbox" name="showOnSignIn" className="h-4 w-4 rounded" />Also on the sign-in page</label>}
            </fieldset>
            <div className="flex flex-wrap gap-3">
              <Field label="Starts" name="startsAt" type="datetime-local" defaultValue={localInput(new Date())} className="w-56" />
              <Field label="Ends (optional)" name="endsAt" type="datetime-local" className="w-56" />
            </div>
            <div className="flex justify-end"><Button type="submit" loading={busy === "create"} disabled={!everyone && !picked.length}>Publish</Button></div>
          </form>
        </Card>
      )}
      <Card className="p-0">
        {data.isLoading ? <div className="flex justify-center p-8 text-brand-600"><Spinner /></div> : !data.data?.announcements.length ? <p className="p-6 text-sm text-slate-500">No announcements yet.</p> : (
          <ul className="divide-y divide-slate-100 dark:divide-slate-800">
            {data.data.announcements.map((a) => (
              <li key={a.id} className="flex flex-wrap items-start gap-3 px-5 py-3">
                <div className="min-w-0 flex-1">
                  <p className="text-sm">{a.message}{a.linkUrl && <> <a href={a.linkUrl} target="_blank" rel="noopener noreferrer" className="text-brand-600 underline">{a.linkText}</a></>}</p>
                  <p className="mt-1 text-xs text-slate-500">
                    {LEVEL_LABEL[a.level]} · {a.orgs ? a.orgs.map((o) => o.name).join(", ") : "everyone"}{a.showOnSignIn ? " · sign-in page" : ""} · from {new Date(a.startsAt).toLocaleString()}{a.endsAt ? ` to ${new Date(a.endsAt).toLocaleString()}` : ""}
                  </p>
                </div>
                <Badge tone={tone(a.state)}>{a.state}</Badge>
                {manage && a.state !== "ended" && <Button size="sm" variant="secondary" onClick={() => void run(`end-${a.id}`, () => api(`${P}/announcements/${a.id}/end`, { method: "POST" }))} loading={busy === `end-${a.id}`}>End now</Button>}
                {manage && <Button size="sm" variant="ghost" className="text-red-600" onClick={() => { if (confirm("Delete this announcement?")) void run(`del-${a.id}`, () => api(`${P}/announcements/${a.id}`, { method: "DELETE" })); }}>Delete</Button>}
              </li>
            ))}
          </ul>
        )}
      </Card>
    </>
  );
}

// ---------------------------------------------------------------- health

type HealthCheck = { id: string; label: string; level: "ok" | "warn" | "critical"; summary: string; details?: { containers?: { service: string; state: string; restarts: number; since: string }[]; lastLogLine?: string; note?: string; load?: number[] } };
type HealthReport = {
  at: string; status: "ok" | "warn" | "critical"; checks: HealthCheck[];
  now: { calls: number; peopleInCalls: number; recordings: number; activeSessions: number };
  app: { version: string; deployedAt: string | null; apiStartedAt: string; nodeMemoryMB: number };
  server: { hostname: string; cpus: number; uptimeSeconds: number } | null;
};
const LEVEL = {
  ok: { label: "OK", dot: "bg-emerald-500", text: "text-emerald-700 dark:text-emerald-300" },
  warn: { label: "Warning", dot: "bg-amber-500", text: "text-amber-700 dark:text-amber-300" },
  critical: { label: "Problem", dot: "bg-red-600", text: "text-red-700 dark:text-red-300" },
};
const duration = (s: number) => (s < 3600 ? `${Math.round(s / 60)} min` : s < 172800 ? `${Math.round(s / 3600)} h` : `${Math.round(s / 86400)} days`);

export function PlatformHealth() {
  const data = useQuery({ queryKey: ["platform-health"], queryFn: () => api<HealthReport>(`${P}/health`), refetchInterval: 15_000 });
  const [open, setOpen] = useState<string | null>(null);
  const h = data.data;
  return (
    <>
      <Header
        title="System health"
        subtitle={h ? <>Checked {new Date(h.at).toLocaleTimeString()} · refreshes every 15 seconds</> : "Checking…"}
        action={h && <span className={cx("flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-semibold ring-1", h.status === "ok" ? "bg-emerald-50 text-emerald-800 ring-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-200 dark:ring-emerald-800" : h.status === "warn" ? "bg-amber-50 text-amber-800 ring-amber-200 dark:bg-amber-900/30 dark:text-amber-200 dark:ring-amber-800" : "bg-red-50 text-red-800 ring-red-200 dark:bg-red-900/30 dark:text-red-200 dark:ring-red-800")}>
          <span className={cx("h-2.5 w-2.5 rounded-full", LEVEL[h.status].dot)} />{h.status === "ok" ? "All good" : h.status === "warn" ? "Needs a look" : "Problem"}
        </span>}
      />
      {data.error && <div className="mb-4"><Alert>{errorText(data.error)}</Alert></div>}
      {!h ? <div className="flex justify-center p-8 text-brand-600"><Spinner /></div> : (
        <>
          <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat label="Calls and meetings now" value={h.now.calls} />
            <Stat label="People in them" value={h.now.peopleInCalls} />
            <Stat label="Recording now" value={h.now.recordings} />
            <Stat label="People active (15 min)" value={h.now.activeSessions} />
          </div>
          <Card className="p-0">
            <ul className="divide-y divide-slate-100 dark:divide-slate-800" aria-label="Health checks">
              {h.checks.map((c) => (
                <li key={c.id} className="px-5 py-3">
                  <div className="flex items-start gap-3">
                    <span className={cx("mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full", LEVEL[c.level].dot)} aria-hidden="true" />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-baseline gap-x-2"><span className="font-medium">{c.label}</span><span className={cx("text-xs font-semibold", LEVEL[c.level].text)}>{LEVEL[c.level].label}</span></div>
                      <p className="text-sm text-slate-600 dark:text-slate-300">{c.summary}</p>
                      {c.details?.note && <p className="mt-0.5 text-xs text-slate-500">{c.details.note}</p>}
                    </div>
                    {c.details?.containers && <button type="button" onClick={() => setOpen((o) => (o === c.id ? null : c.id))} className="text-sm text-brand-600 hover:underline">{open === c.id ? "Hide" : "Details"}</button>}
                  </div>
                  {open === c.id && c.details?.containers && (
                    <div className="ml-5 mt-3 overflow-x-auto"><table className="text-sm">
                      <tbody>{c.details.containers.map((x) => (
                        <tr key={x.service}><td className="pr-4 font-medium">{x.service}</td><td className="pr-4">{x.state}</td><td className="pr-4 text-slate-500">since {new Date(x.since).toLocaleString()}</td><td className="text-slate-500">{x.restarts} restart{x.restarts === 1 ? "" : "s"}</td></tr>
                      ))}</tbody>
                    </table></div>
                  )}
                </li>
              ))}
            </ul>
          </Card>
          <p className="mt-4 text-xs text-slate-500">
            Version {h.app.version}{h.app.deployedAt ? `, deployed ${new Date(h.app.deployedAt).toLocaleString()}` : ""} · API up since {new Date(h.app.apiStartedAt).toLocaleString()} ({h.app.nodeMemoryMB} MB)
            {h.server && <> · server {h.server.hostname}, {h.server.cpus} CPUs, up {duration(h.server.uptimeSeconds)}</>}
          </p>
        </>
      )}
    </>
  );
}

// ---------------------------------------------------------------- plans

type PlanRow = {
  id: string; name: string; description: string; maxMembers: number; maxCallParticipants: number; groupCallMinutes: number | null;
  storageGb: number; recording: boolean; transcripts: boolean; maxProjects: number | null; freeClients: boolean; monthlyPrice: number | null; yearlyPrice: number | null; currency: string;
  stripePriceMonthly: string | null; stripePriceYearly: string | null; trialDays: number; allowDiscountCodes: boolean;
  visible: boolean; isDefault: boolean; sortOrder: number; archived: boolean; paying: number; given: number;
};
const cents = (n: number | null, currency: string) => (n === null ? "—" : new Intl.NumberFormat(undefined, { style: "currency", currency: currency.toUpperCase() }).format(n / 100));

export function PlatformPlans() {
  const can = useCan();
  const qc = useQueryClient();
  const data = useQuery({ queryKey: ["platform-plans"], queryFn: () => api<{ stripe: boolean; plans: PlanRow[] }>(`${P}/plans`) });
  const [editing, setEditing] = useState<PlanRow | "new" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const manage = can("plans.manage");
  async function act(fn: () => Promise<{ plans: PlanRow[] }>) {
    setError(null);
    try { const r = await fn(); qc.setQueryData(["platform-plans"], (old: { stripe: boolean } | undefined) => ({ ...old, ...r })); } catch (err) { setError(errorText(err)); }
  }
  return (
    <>
      <Header title="Plans" subtitle="What customers can be on: limits, prices, trials. Limit changes apply to everyone on the plan straight away." action={manage && <Button onClick={() => { setWarnings([]); setEditing("new"); }}><Icon name="plus" className="h-4 w-4" />New plan</Button>} />
      {error && <div className="mb-4"><Alert>{error}</Alert></div>}
      {warnings.length > 0 && <div className="mb-4"><Alert kind="info">Saved. Check these with Stripe: {warnings.join(" ")}</Alert></div>}
      {data.data && !data.data.stripe && <div className="mb-4"><Alert kind="info">Online payment isn't connected on this server: plans show their prices, but customers can't buy them until it is (you can still give plans).</Alert></div>}
      <Card className="overflow-x-auto p-0">
        {data.isLoading ? <div className="flex justify-center p-8 text-brand-600"><Spinner /></div> : (
          <table className="w-full min-w-[820px] text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500 dark:bg-slate-800/50">
              <tr><th className="px-4 py-2">Plan</th><th className="px-4 py-2">Price / person</th><th className="px-4 py-2">Limits</th><th className="px-4 py-2 text-right">Customers</th><th className="px-4 py-2" /></tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {data.data?.plans.map((p) => (
                <tr key={p.id} className={cx(p.archived && "opacity-60")}>
                  <td className="px-4 py-3"><div className="font-medium">{p.name} <span className="font-normal text-slate-500">/{p.id}</span></div>
                    <div className="mt-1 flex flex-wrap gap-1">{p.isDefault && <Badge tone="brand">Default (free)</Badge>}{!p.visible && <Badge>Hidden</Badge>}{p.archived && <Badge tone="amber">Archived</Badge>}{p.trialDays > 0 && <Badge>{p.trialDays}-day trial</Badge>}</div></td>
                  <td className="px-4 py-3">{p.isDefault ? "Free" : <>{cents(p.monthlyPrice, p.currency)} / month<br /><span className="text-slate-500">{cents(p.yearlyPrice, p.currency)} / year</span></>}</td>
                  <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{p.maxMembers} people · {p.maxCallParticipants} per call · {p.groupCallMinutes ? `${p.groupCallMinutes} min meetings` : "no time limit"} · {p.storageGb} GB{p.recording ? " · recording" : ""}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{p.paying} paying{p.given ? <span className="text-slate-500"> · {p.given} given</span> : ""}</td>
                  <td className="px-4 py-3 text-right">{manage && (
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="secondary" onClick={() => { setWarnings([]); setEditing(p); }}>Edit</Button>
                      {!p.isDefault && <Button size="sm" variant="ghost" onClick={() => void act(() => api(`${P}/plans/${p.id}/archive`, { method: "POST", body: { archived: !p.archived } }))}>{p.archived ? "Restore" : "Archive"}</Button>}
                    </div>
                  )}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
      <p className="mt-3 text-xs text-slate-500">Archived plans keep working for the customers on them; nobody new can choose them. Discount codes are created in Stripe (Products → Coupons → Promotion codes); turn them on per plan here.</p>
      {editing && <PlanDialog plan={editing === "new" ? null : editing} onClose={() => setEditing(null)} onSaved={(r) => { qc.setQueryData(["platform-plans"], (old: { stripe: boolean } | undefined) => ({ ...old, plans: r.plans })); setWarnings(r.warnings); setEditing(null); }}
        onMakeDefault={(id) => { void act(() => api(`${P}/plans/${id}/default`, { method: "POST" })); setEditing(null); }} />}
    </>
  );
}

function PlanDialog({ plan, onClose, onSaved, onMakeDefault }: { plan: PlanRow | null; onClose: () => void; onSaved: (r: { plans: PlanRow[]; warnings: string[] }) => void; onMakeDefault: (id: string) => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [unlimited, setUnlimited] = useState(plan ? plan.groupCallMinutes === null : true);
  async function save(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const d = new FormData(e.currentTarget);
    const num = (k: string) => Number(d.get(k));
    const money = (k: string) => (String(d.get(k) ?? "").trim() === "" ? null : Math.round(Number(d.get(k)) * 100));
    const body = {
      name: String(d.get("name")), description: String(d.get("description") ?? ""),
      maxMembers: num("maxMembers"), maxCallParticipants: num("maxCallParticipants"), groupCallMinutes: unlimited ? null : num("groupCallMinutes"),
      storageGb: num("storageGb"), recording: d.get("recording") === "on", transcripts: d.get("transcripts") === "on",
      maxProjects: String(d.get("maxProjects") ?? "").trim() === "" ? null : num("maxProjects"), freeClients: d.get("freeClients") === "on",
      monthlyPrice: plan?.isDefault ? null : money("monthlyPrice"), yearlyPrice: plan?.isDefault ? null : money("yearlyPrice"), currency: String(d.get("currency")),
      stripePriceMonthly: plan?.isDefault ? null : String(d.get("stripePriceMonthly") ?? "") || null, stripePriceYearly: plan?.isDefault ? null : String(d.get("stripePriceYearly") ?? "") || null,
      trialDays: num("trialDays"), allowDiscountCodes: d.get("allowDiscountCodes") === "on", visible: d.get("visible") === "on",
    };
    setBusy(true); setError(null); setFields({});
    try {
      const r = plan
        ? await api<{ plans: PlanRow[]; warnings: string[] }>(`${P}/plans/${plan.id}`, { method: "PUT", body })
        : await api<{ plans: PlanRow[]; warnings: string[] }>(`${P}/plans`, { method: "POST", body: { id: String(d.get("id")), ...body } });
      onSaved(r);
    } catch (err) { setError(errorText(err)); if (err instanceof ApiError) setFields(err.fields); setBusy(false); }
  }
  const p = plan;
  const isDefault = Boolean(p?.isDefault);
  return (
    <Dialog open onClose={onClose} title={p ? `Edit ${p.name}` : "New plan"}>
      <form onSubmit={save} className="max-h-[70vh] space-y-4 overflow-y-auto pr-1">
        {error && <Alert>{error}</Alert>}
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Name" name="name" defaultValue={p?.name ?? ""} required maxLength={40} error={fields.name} />
          {p ? <Field label="Id (fixed)" value={p.id} readOnly disabled /> : <Field label="Id" name="id" required placeholder="business" pattern="[a-z][a-z0-9\-]{1,29}" hint="Lower-case; can't change later." error={fields.id} />}
        </div>
        <Field label="Short description" name="description" defaultValue={p?.description ?? ""} maxLength={200} />
        <fieldset className="grid gap-3 sm:grid-cols-3">
          <legend className="mb-2 text-sm font-semibold">Limits</legend>
          <Field label="People" name="maxMembers" type="number" min={1} max={100000} defaultValue={p?.maxMembers ?? 50} required />
          <Field label="Per call / meeting" name="maxCallParticipants" type="number" min={2} max={50} defaultValue={p?.maxCallParticipants ?? 25} required />
          <Field label="Storage (GB)" name="storageGb" type="number" min={1} max={100000} defaultValue={p?.storageGb ?? 20} required />
          <Field label="Projects at a time" name="maxProjects" type="number" min={1} max={100000} defaultValue={p?.maxProjects ?? ""} hint="Empty: no limit" />
          <div className="sm:col-span-2">
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={unlimited} onChange={(e) => setUnlimited(e.target.checked)} className="h-4 w-4 rounded" />No time limit on meetings</label>
            {!unlimited && <Field label="Meeting / group call limit (minutes)" name="groupCallMinutes" type="number" min={5} max={1440} defaultValue={p?.groupCallMinutes ?? 60} className="mt-2" />}
          </div>
          <div className="flex flex-wrap gap-4 self-end text-sm">
            <label className="flex items-center gap-2"><input type="checkbox" name="recording" defaultChecked={p?.recording ?? true} className="h-4 w-4 rounded" />Recording</label>
            <label className="flex items-center gap-2"><input type="checkbox" name="transcripts" defaultChecked={p?.transcripts ?? true} className="h-4 w-4 rounded" />Transcripts</label>
            <label className="flex items-center gap-2" title="Guests who are a project's client take no place on the plan and are not paid seats"><input type="checkbox" name="freeClients" defaultChecked={p?.freeClients ?? false} className="h-4 w-4 rounded" />Clients are free</label>
          </div>
        </fieldset>
        {!isDefault && (
          <fieldset className="grid gap-3 sm:grid-cols-3">
            <legend className="mb-2 text-sm font-semibold">Price per person</legend>
            <Field label="Monthly" name="monthlyPrice" type="number" min={0} step="0.01" defaultValue={p?.monthlyPrice !== null && p?.monthlyPrice !== undefined ? (p.monthlyPrice / 100).toString() : ""} placeholder="6.00" />
            <Field label="Yearly" name="yearlyPrice" type="number" min={0} step="0.01" defaultValue={p?.yearlyPrice !== null && p?.yearlyPrice !== undefined ? (p.yearlyPrice / 100).toString() : ""} placeholder="60.00" />
            <Field label="Currency" name="currency" defaultValue={(p?.currency ?? "usd").toUpperCase()} maxLength={3} required />
            <Field label="Stripe price id (monthly)" name="stripePriceMonthly" defaultValue={p?.stripePriceMonthly ?? ""} placeholder="price_1Abc…" error={fields.stripePriceMonthly} className="sm:col-span-3" />
            <Field label="Stripe price id (yearly)" name="stripePriceYearly" defaultValue={p?.stripePriceYearly ?? ""} placeholder="price_1Abc…" error={fields.stripePriceYearly} className="sm:col-span-3" />
            <Field label="Free trial (days)" name="trialDays" type="number" min={0} max={90} defaultValue={p?.trialDays ?? 0} />
            <label className="flex items-center gap-2 self-end text-sm sm:col-span-2"><input type="checkbox" name="allowDiscountCodes" defaultChecked={p?.allowDiscountCodes ?? true} className="h-4 w-4 rounded" />Allow discount codes at checkout</label>
          </fieldset>
        )}
        {isDefault && <><input type="hidden" name="currency" value={p!.currency} /><input type="hidden" name="trialDays" value="0" /><p className="text-sm text-slate-500">This is the default plan: it's free and always shown.</p></>}
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" name="visible" defaultChecked={p?.visible ?? true} disabled={isDefault} className="h-4 w-4 rounded" />Shown on customers' Billing page</label>
        {isDefault && <input type="hidden" name="visible" value="on" />}
        {p && (p.paying + p.given > 0) && <Alert kind="info">{p.paying + p.given} organization{p.paying + p.given === 1 ? " is" : "s are"} on this plan: changed limits apply to them straight away. Price changes only affect new checkouts (change existing ones in Stripe).</Alert>}
        <div className="flex flex-wrap justify-between gap-2">
          {p && !isDefault && !p.monthlyPrice && !p.yearlyPrice && !p.archived ? <Button type="button" variant="ghost" onClick={() => onMakeDefault(p.id)}>Make this the default plan</Button> : <span />}
          <div className="flex gap-2"><Button type="button" variant="secondary" onClick={onClose}>Cancel</Button><Button type="submit" loading={busy}>{p ? "Save" : "Create plan"}</Button></div>
        </div>
      </form>
    </Dialog>
  );
}
